import { Alert, Flex, Stack, Text } from "@mantine/core";
import { ISpecificationTable } from "@mctypes/frontend.types";
import { FlatHierarchy, IPNSProps } from "@mctypes/global.types";
import { ITitleAndDescription } from "@schemas/issueSchema";
import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  useGetIssueboardFavoritesQuery,
  useGetProjectByProjectIdIssueboardQuery,
  useGetProjectByProjectIdIssueboardTemplateAllQuery,
  usePostProjectByProjectIdIssueboardImportExcelMutation,
  usePostProjectByProjectIdIssueboardMutation,
} from "../../../../openapi";
import { apiSlice } from "../../../apiSlice";
import { FailedAlert } from "../../../components";
import { emptyValidator } from "../../../components/ExcelImportValidator/ExcelImportValidator.utils";
import HierarchyTreeSelector from "../../../components/HierarchyTreeSelector/HierarchyTreeSelector";
import McInputNew from "../../../components/McInput/McInputNew";
import TemplateSelector, { ITemplate } from "../../../components/TemplateSelector/TemplateSelector";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { MCApplicationsUrlEnum } from "@mcapps/shared/sharedConstants";
import IssueBoardsTable from "./IssueBoardsTable";
import { setIssueSelectedPnsBoardFilterItemInRedux } from "./issueSlice";

const IssueBoards: React.FC = () => {
  let history = useHistory();

  const projectId = useAppSelector((state) => state.globalReducer.currentProjectId);
  useGetIssueboardFavoritesQuery({
    projectId: projectId,
  });

  const [modalIsOpen, setModalOpen] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState<string>("");

  const dispatch = useAppDispatch();

  const goToBoard = async (board: any) => {
    dispatch(apiSlice.util.invalidateTags(["IssueBoard"]));
    history.push(`/project/${project._id}/issues/${board._id}`);
  };

  const project = useAppSelector((state) => state.adminReducer.project);

  const {
    data: issueBoards = [],
    isLoading: issueBoardsLoading,
    error: boardError,
  } = useGetProjectByProjectIdIssueboardQuery(
    {
      projectId: project._id,
    },
    { refetchOnMountOrArgChange: true },
  );

  const {
    data: issueBoardTemplates = [],
    isLoading: templateLoading,
    error: templateError,
    refetch: refetchIssueBoardTemplates,
  } = useGetProjectByProjectIdIssueboardTemplateAllQuery(
    {
      projectId: project._id,
    },
    { refetchOnMountOrArgChange: true },
  );

  const convertedIssueBoardsToTemplates: ITemplate[] = issueBoards
    .filter((board) => board.status !== "DELETED")
    .map((board) => {
      return {
        _id: board._id,
        name: board.name,
        templateType: "INSTANCE",
      };
    });

  // TODO: Category is hardcoded to BREEAM, should be dynamic when we get more categories
  const convertedIssueBoardTemplates: ITemplate[] = issueBoardTemplates
    .filter((template) => template.template?.status === "AKTIV")
    .map((template) => {
      return {
        _id: template._id,
        name: template.name,
        templateType: "MC",
        category: template.category,
        description: template.description,
        admin: template.template?.admin,
      };
    });

  const combinedTemplates = [...convertedIssueBoardsToTemplates, ...convertedIssueBoardTemplates];

  const [createBoard, { isLoading: createBoardLoading, error: createBoardError }] =
    usePostProjectByProjectIdIssueboardMutation();

  const [createBoardFromExcel, { isLoading: createBoardFromExcelLoading, error: createBoardFromExcelError }] =
    usePostProjectByProjectIdIssueboardImportExcelMutation();

  const createNew = async (boardIdToCopySettingsFrom?: string, copyIssuesAndTasks?: boolean) => {
    const createdBoard = await createBoard({
      projectId: project._id,
      body: {
        name: selectedTitle,
        boardIdToCopySettingsFrom: boardIdToCopySettingsFrom,
        copyIssuesAndTasks: copyIssuesAndTasks,
      },
    }).unwrap();
    setModalOpen(false);
    goToBoard(createdBoard);
  };

  const toggle = () => {
    setSelectedTitle("");
    setModalOpen(!modalIsOpen);
  };

  const createIssueBoardFromExcelImport = async (titleAndDescriptions: ITitleAndDescription[]) => {
    const titleAndDescriptionsConverted = titleAndDescriptions.map((item) => {
      return {
        title: String(item.title),
        description: String(item.description),
      };
    });
    const createdBoard = await createBoardFromExcel({
      projectId: project._id,
      body: {
        nameOfBoard: selectedTitle,
        titleAndDescriptions: titleAndDescriptionsConverted,
      },
    }).unwrap();
    setModalOpen(false);
    goToBoard(createdBoard);
  };

  const isLoading = issueBoardsLoading || templateLoading || createBoardLoading || createBoardFromExcelLoading;
  const error = boardError || templateError || createBoardError || createBoardFromExcelError;

  const isTemplateProject = project.maconomy.projectNo === 100;

  const excelSpecificationTable: ISpecificationTable = {
    columns: [
      {
        field: "title",
        headerName: "Tittel",
      },
      {
        field: "description",
        headerName: "Beskrivelse",
      },
    ],
    data: [
      {
        title: "LCC beregning av valgt materiale..",
        description: "ARK må lage en LC....",
      },
      {
        title: "Dører til tekniske rom",
        description: "Se vedlagt oversikt over teknisk stand....",
      },
      {
        title: "...",
        description: "....",
      },
    ],
  };

  const excelImport = {
    enabled: true,
    specificationTable: excelSpecificationTable,
    onCreateFromExcel: (data) => {
      const titleAndDescriptions = data.map((row) => {
        return {
          title: row[0],
          description: row[1],
        };
      });
      createIssueBoardFromExcelImport(titleAndDescriptions);
    },
    zodValidation: [emptyValidator, emptyValidator],
  };

  const { selectedPnsBoardFilterItem } = useAppSelector((state) => state.issueReducerNew);

  const pnsItems = useMemo(() => {
    return (
      (project?.pns as FlatHierarchy<IPNSProps>)?.map((pns) => ({
        ...pns,
        comboLabel: `${pns.props.pnsId} - ${pns.props.title}`,
      })) ?? []
    );
  }, [project?.pns]);

  const selectedPnsIds = useMemo(() => {
    const id = pnsItems.find((item) => item.props.pnsId === selectedPnsBoardFilterItem)?._id;
    return id != null ? [id] : [];
  }, [selectedPnsBoardFilterItem, pnsItems]);

  const PNS_COLLAPSE_LOCAL_STORAGE_KEY = `${project._id}_issue_pnsCollapse`;

  const pnsIsOpen: boolean = useMemo(() => {
    const valInLocalStorage = localStorage.getItem(PNS_COLLAPSE_LOCAL_STORAGE_KEY);
    if (valInLocalStorage === null) {
      return true;
    }
    return valInLocalStorage === "true";
  }, [PNS_COLLAPSE_LOCAL_STORAGE_KEY]);

  return (
    <Stack gap={0} h="100%" w="100%" style={{ overflow: "auto" }}>
      <>
        {modalIsOpen === true && (
          <TemplateSelector
            templates={isTemplateProject === false ? combinedTemplates : convertedIssueBoardTemplates}
            header={isTemplateProject ? "Ny mal" : "Nytt register"}
            app={{
              name: MCApplicationsUrlEnum.ISSUE,
              icon: "appIcons/saks-sm.svg",
              iconColor: "#659180",
            }}
            onCreate={(template) => {
              createNew(template._id, template._copyIssuesAndTasks);
            }}
            closeModal={toggle}
            isTemplateProject={isTemplateProject}
            isTitleEmpty={selectedTitle === "" ? true : false}
            descriptionAndAdmin={true}
            showInstanceButton={isTemplateProject === false ? true : false}
            showProjectButton={false}
            preSelectedTemplate={
              isTemplateProject === false
                ? combinedTemplates.find((template) => template.name === "Standard")
                : convertedIssueBoardTemplates.find((template) => template.name === "Standard")
            }
            disableOpprettButton={selectedTitle === ""}
            createLoading={createBoardLoading || createBoardFromExcelLoading}
            excelImport={excelImport}
          >
            <Flex direction="column" pl="5rem" pr="5rem" pt="1rem" style={{ background: "FBFBFB", flexGrow: 1 }}>
              <Flex component="h4" m="0.5rem" justify="center" align="center" className="bold">
                NYTT REGISTER{" "}
              </Flex>
              <div className="grid-container">
                <Text fw={700} mb={0} style={{ display: "flex", alignItems: "center" }}>
                  Navn*
                </Text>
                <McInputNew
                  required
                  autoFocus
                  onChange={(t) => setSelectedTitle(t.target.value)}
                  value={selectedTitle}
                  placeholder={isTemplateProject ? "Angi navnet på malen" : "Angi navnet på registeret"}
                />
              </div>
            </Flex>
          </TemplateSelector>
        )}

        {error != null && (
          <Flex w="100%" p="xs">
            <FailedAlert />
          </Flex>
        )}

        {project?.maconomy?.projectNo === 100 && (
          <Alert color="red" m="xs">
            <Text c="red.7" fw="bold" size="sm">
              Alle registre i dette prosjektet vil være tilgjengelige som maler i andre prosjekter!
            </Text>
          </Alert>
        )}

        <Flex h="100%" gap="xs">
          {pnsItems?.length > 0 && (
            <HierarchyTreeSelector
              style={{
                minWidth: "200px",
                maxWidth: "300px",
                background: "#ededed",
              }}
              nodeList={pnsItems}
              idProp={"pnsId"}
              titleProp={"title"}
              onSelectionChange={(node) => dispatch(setIssueSelectedPnsBoardFilterItemInRedux(node[0]?.props?.pnsId))}
              selectedNodeIds={selectedPnsIds}
              isCollapsed={!pnsIsOpen}
              localStorageCollapseKey={PNS_COLLAPSE_LOCAL_STORAGE_KEY}
            />
          )}
          <Flex flex={1} mt="xs">
            <IssueBoardsTable addNew={toggle} />
          </Flex>
        </Flex>
      </>
    </Stack>
  );
};

export default IssueBoards;
